import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  List,
  Modal,
  Radio,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { CheckOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { Tv } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  genresApi,
  seriesDatasourceApi,
  streamingPlatformsApi,
  type DatasourceCandidate,
  type SeriesInfoResponse,
} from "../services/api";

interface AISeriesHelperProps {
  onAccept: (seriesInfo: SeriesInfoResponse) => void;
}

export function AISeriesHelper({ onAccept }: AISeriesHelperProps) {
  const [searchTitle, setSearchTitle] = useState("");
  const [candidates, setCandidates] = useState<DatasourceCandidate[]>([]);
  const [seriesInfo, setSeriesInfo] = useState<SeriesInfoResponse | null>(null);
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentProgress, setCurrentProgress] = useState("");
  const [progressMessages, setProgressMessages] = useState<string[]>([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  const { data: genres } = useQuery({
    queryKey: ["genres-all"],
    queryFn: () => genresApi.list({ page_size: 100 }),
  });

  const { data: streamingPlatforms } = useQuery({
    queryKey: ["streaming-platforms-all"],
    queryFn: () => streamingPlatformsApi.list({ page_size: 100 }),
  });

  useEffect(() => {
    if (!isFetching) {
      setElapsedSeconds(0);
      return;
    }

    const startedAt = Date.now();
    setElapsedSeconds(0);
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isFetching]);

  const imageOptions = useMemo(() => {
    const fromCandidates = seriesInfo?.image_candidates || [];
    if (fromCandidates.length > 0) return fromCandidates;
    if (seriesInfo?.cover_url) {
      return [
        {
          image_id: "cover_url",
          source: "datasource",
          url: seriesInfo.cover_url,
          thumbnail_url: seriesInfo.cover_url,
          label: "Cover",
          kind: "cover",
        },
      ];
    }
    return [];
  }, [seriesInfo]);

  const matchGenresAndPlatforms = (data: SeriesInfoResponse) => {
    const matchedGenreIds: string[] = [];
    const matchedPlatformIds: string[] = [];

    if (data.suggested_genres && genres?.items) {
      data.suggested_genres.forEach((genreName) => {
        const lower = genreName.toLowerCase();
        const match = genres.items.find((g) => {
          const name = g.name.toLowerCase();
          return name === lower || name.includes(lower) || lower.includes(name);
        });
        if (match) matchedGenreIds.push(match.id);
      });
    }

    if (data.suggested_streaming_platforms && streamingPlatforms?.items) {
      data.suggested_streaming_platforms.forEach((platformName) => {
        const lower = platformName.toLowerCase();
        const match = streamingPlatforms.items.find((p) => {
          const name = p.name.toLowerCase();
          return name === lower || name.includes(lower) || lower.includes(name);
        });
        if (match) matchedPlatformIds.push(match.id);
      });
    }

    return { ...data, genre_ids: matchedGenreIds, streaming_platform_ids: matchedPlatformIds };
  };

  const formatProgressMessage = (msg: string): string => {
    try {
      const parsed = JSON.parse(msg);
      return parsed.message || parsed.source || msg;
    } catch {
      return msg.replace(/^["']|["']$/g, "");
    }
  };

  const handleSearch = async () => {
    const title = searchTitle.trim();
    if (!title) {
      message.warning("Wprowadz tytul serialu");
      return;
    }

    setIsFetching(true);
    setCurrentProgress("Szukanie kandydatow...");
    setProgressMessages([]);

    try {
      const result = await seriesDatasourceApi.fetchSeriesCandidates({ title });
      const items = result.items || [];
      if (items.length === 0) {
        message.warning("Nie znaleziono kandydatow");
        return;
      }
      setCandidates(items);
      setIsCandidateModalOpen(true);
    } catch (error: unknown) {
      message.error(error instanceof Error && error.message ? error.message : "Nie udalo sie wyszukac kandydatow");
    } finally {
      setIsFetching(false);
      setCurrentProgress("");
    }
  };

  const handleCandidateSelect = async (candidate: DatasourceCandidate) => {
    setIsCandidateModalOpen(false);
    setIsFetching(true);
    setProgressMessages([]);
    setCurrentProgress("Pobieranie szczegolow...");

    try {
      const data = await seriesDatasourceApi.fetchSeriesDetailsStreaming(
        { title: searchTitle.trim(), candidate },
        (progressMsg) => {
          const formatted = formatProgressMessage(progressMsg);
          setCurrentProgress(formatted);
          setProgressMessages((prev) => [...prev, formatted]);
        },
      );
      const enriched = matchGenresAndPlatforms(data);
      const defaultImage = enriched.image_candidates?.[0]?.url || enriched.cover_url || null;
      setSelectedImageUrl(defaultImage);
      setSeriesInfo(enriched);
      setIsDetailsModalOpen(true);
    } catch (error: unknown) {
      message.error(error instanceof Error && error.message ? error.message : "Nie udalo sie pobrac szczegolow");
    } finally {
      setIsFetching(false);
      setCurrentProgress("");
      setProgressMessages([]);
    }
  };

  const handleAccept = () => {
    if (!seriesInfo) return;
    onAccept({ ...seriesInfo, cover_url: selectedImageUrl || seriesInfo.cover_url });
    setIsDetailsModalOpen(false);
    setSearchTitle("");
    setCandidates([]);
    setSeriesInfo(null);
    setSelectedImageUrl(null);
    message.success("Dane zostaly wypelnione w formularzu");
  };

  return (
    <>
      <Card
        title={
          <Space>
            <Tv size={14} />
            <span>Asystent danych - Pobierz informacje o serialu</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="Wprowadz tytul serialu"
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isFetching}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={isFetching}>
            Szukaj
          </Button>
        </Space.Compact>

        {isFetching && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <Spin />
            <Space style={{ marginTop: 8 }}>
              <span>{currentProgress || "Pracuje..."}</span>
              <Tag color="processing">Czas: {elapsedSeconds} s</Tag>
            </Space>
            {progressMessages.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#595959" }}>
                {progressMessages.slice(-4).map((msg, idx) => (
                  <div key={`${msg}-${idx}`}>{msg}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <Alert
          title="Zrodlo danych"
          description="Najpierw zobaczysz liste kandydatow. Po wyborze serialu pobierzemy szczegoly i plakaty."
          type="info"
          showIcon
          style={{ marginTop: 12 }}
        />
      </Card>

      <Modal
        title="Wybierz serial"
        open={isCandidateModalOpen}
        onCancel={() => setIsCandidateModalOpen(false)}
        footer={null}
        width={900}
      >
        <List
          itemLayout="horizontal"
          dataSource={candidates}
          renderItem={(candidate) => (
            <List.Item
              actions={[
                <Button key="select" type="primary" onClick={() => handleCandidateSelect(candidate)}>
                  Wybierz
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  candidate.thumbnail_url ? (
                    <img src={candidate.thumbnail_url} alt="" style={{ width: 56, height: 84, objectFit: "cover" }} />
                  ) : null
                }
                title={
                  <Space wrap>
                    <Typography.Text strong>{candidate.title || "Bez tytulu"}</Typography.Text>
                    {candidate.year && <Tag>{candidate.year}</Tag>}
                    <Tag color="blue">{candidate.source}</Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={2}>
                    {candidate.original_title && (
                      <Typography.Text type="secondary">{candidate.original_title}</Typography.Text>
                    )}
                    {candidate.description && (
                      <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                        {candidate.description}
                      </Typography.Paragraph>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <Tv size={14} />
            <span>Dane pobrane z zewnetrznych zrodel</span>
          </Space>
        }
        open={isDetailsModalOpen}
        onOk={handleAccept}
        onCancel={() => {
          setIsDetailsModalOpen(false);
          setSeriesInfo(null);
          setSelectedImageUrl(null);
        }}
        width={800}
        okText="Akceptuj i wypelnij formularz"
        cancelText="Anuluj"
        okButtonProps={{ icon: <CheckOutlined /> }}
        cancelButtonProps={{ icon: <CloseOutlined /> }}
      >
        {seriesInfo && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Tytul">{seriesInfo.title || <i>Brak danych</i>}</Descriptions.Item>
            <Descriptions.Item label="Tytul oryginalny">
              {seriesInfo.original_title || <i>Brak danych</i>}
            </Descriptions.Item>
            <Descriptions.Item label="Rok rozpoczecia">
              {seriesInfo.start_year || <i>Brak danych</i>}
            </Descriptions.Item>
            <Descriptions.Item label="Rok zakonczenia">
              {seriesInfo.end_year || <i>W produkcji</i>}
            </Descriptions.Item>
            <Descriptions.Item label="Opis">{seriesInfo.description || <i>Brak danych</i>}</Descriptions.Item>
            <Descriptions.Item label="Liczba sezonow">
              {seriesInfo.seasons_count || <i>Brak danych</i>}
            </Descriptions.Item>
            <Descriptions.Item label="Liczba odcinkow">
              {seriesInfo.episodes_count || <i>Brak danych</i>}
            </Descriptions.Item>
            <Descriptions.Item label="Tworcy">
              {seriesInfo.suggested_creators?.length ? (
                <Space wrap>{seriesInfo.suggested_creators.map((creator) => <Tag key={creator}>{creator}</Tag>)}</Space>
              ) : (
                <i>Brak danych</i>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Gatunki">
              {seriesInfo.suggested_genres?.length ? (
                <Space wrap>{seriesInfo.suggested_genres.map((genre) => <Tag key={genre}>{genre}</Tag>)}</Space>
              ) : (
                <i>Brak danych</i>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Platformy">
              {seriesInfo.suggested_streaming_platforms?.length ? (
                <Space wrap>
                  {seriesInfo.suggested_streaming_platforms.map((platform) => (
                    <Tag key={platform}>{platform}</Tag>
                  ))}
                </Space>
              ) : (
                <i>Brak danych</i>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Plakat / okladka">
              {imageOptions.length ? (
                <Radio.Group value={selectedImageUrl} onChange={(e) => setSelectedImageUrl(e.target.value)}>
                  <Space wrap align="start">
                    {imageOptions.map((image) => (
                      <Radio.Button key={image.image_id} value={image.url} style={{ height: "auto", padding: 8 }}>
                        <Space direction="vertical" size={4}>
                          <img
                            src={image.thumbnail_url || image.url}
                            alt={image.label || "Plakat"}
                            style={{ width: 90, height: 135, objectFit: "contain", border: "1px solid #d9d9d9" }}
                          />
                          <Typography.Text style={{ maxWidth: 90 }} ellipsis>
                            {image.source}
                          </Typography.Text>
                        </Space>
                      </Radio.Button>
                    ))}
                  </Space>
                </Radio.Group>
              ) : (
                <i>Brak danych</i>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
}
